import React, { useContext } from 'react';
import dashboardContext from '../../../context/dashboard/dashboardContext';
import DashboardLayout from '../shared/DashboardLayout';
import pdfGenerator from '../../../utils/pdfGenerator';
import { formatIndianCurrency } from '../../../utils/currencyFormatter';
import './ReportGenerator.css';

const ReportGenerator = () => {
  const { dashboardData, selectedRestaurant, restaurants } = useContext(dashboardContext);
  
  const currentRestaurant = restaurants.find(r => r.id === selectedRestaurant) || restaurants[0];

  const handleDownloadReport = () => {
    pdfGenerator.downloadReport(dashboardData, currentRestaurant);
  };

  const { summary, salesMetrics, topItems, customerMetrics, experiments } = dashboardData;

  return (
    <DashboardLayout 
      title="Reports" 
      subtitle="Generate comprehensive performance reports"
    >
      <div className="report-generator">
        <div className="report-preview-card">
          <div className="preview-header">
            <h2>📄 Bi-Weekly Performance Report</h2>
            <button className="download-btn" onClick={handleDownloadReport}>
              <span className="btn-icon">⬇️</span>
              Download PDF Report
            </button>
          </div>

          <div className="preview-content">
            <div className="preview-section">
              <h3>Report Contents</h3>
              <ul className="report-checklist">
                <li className="checklist-item">
                  <span className="check-icon">✓</span>
                  <span>Executive Summary with key metrics</span>
                </li>
                <li className="checklist-item">
                  <span className="check-icon">✓</span>
                  <span>Sales Performance & Revenue Analysis</span>
                </li>
                <li className="checklist-item">
                  <span className="check-icon">✓</span>
                  <span>Top & Bottom Performing Menu Items</span>
                </li>
                <li className="checklist-item">
                  <span className="check-icon">✓</span>
                  <span>Cost & Profitability Breakdown</span>
                </li>
                <li className="checklist-item">
                  <span className="check-icon">✓</span>
                  <span>Staff Performance Leaderboards</span>
                </li>
                <li className="checklist-item">
                  <span className="check-icon">✓</span>
                  <span>Customer Satisfaction & Feedback</span>
                </li>
                <li className="checklist-item">
                  <span className="check-icon">✓</span>
                  <span>Experiments & A/B Test Results</span>
                </li>
                <li className="checklist-item">
                  <span className="check-icon">✓</span>
                  <span>Actionable Recommendations</span>
                </li>
              </ul>
            </div>

            <div className="preview-section">
              <h3>Report Highlights</h3>
              
              <div className="highlight-cards">
                <div className="highlight-card">
                  <span className="highlight-icon">💰</span>
                  <div className="highlight-content">
                    <h4>Revenue Performance</h4>
                    <p className="highlight-value">{formatIndianCurrency(summary?.todayRevenue || 0, true, 2)}</p>
                    <p className="highlight-change positive">
                      {summary?.revenueChange >= 0 ? '↑' : '↓'} {Math.abs(summary?.revenueChange || 0)}% vs previous period
                    </p>
                  </div>
                </div>

                <div className="highlight-card">
                  <span className="highlight-icon">🍽️</span>
                  <div className="highlight-content">
                    <h4>Top Selling Item</h4>
                    <p className="highlight-value">{topItems?.[0]?.name || 'N/A'}</p>
                    <p className="highlight-sub">{topItems?.[0]?.quantity || 0} units sold</p>
                  </div>
                </div>

                <div className="highlight-card">
                  <span className="highlight-icon">😊</span>
                  <div className="highlight-content">
                    <h4>Customer Satisfaction</h4>
                    <p className="highlight-value">⭐ {customerMetrics?.customerSatisfactionScore || 0}/5.0</p>
                    <p className="highlight-sub">{customerMetrics?.reviewsCount || 0} reviews</p>
                  </div>
                </div>

                <div className="highlight-card">
                  <span className="highlight-icon">🧪</span>
                  <div className="highlight-content">
                    <h4>Active Experiments</h4>
                    <p className="highlight-value">{experiments?.length || 0}</p>
                    <p className="highlight-sub">Ongoing tests & initiatives</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="preview-section">
              <h3>Report Features</h3>
              <div className="features-grid">
                <div className="feature-item">
                  <span className="feature-icon">📊</span>
                  <h4>Comprehensive Data</h4>
                  <p>All key metrics and KPIs in one document</p>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📈</span>
                  <h4>Visual Tables</h4>
                  <p>Easy-to-read formatted tables and data</p>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">💡</span>
                  <h4>AI Recommendations</h4>
                  <p>Actionable insights based on your data</p>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📱</span>
                  <h4>Mobile Friendly</h4>
                  <p>Readable on all devices</p>
                </div>
              </div>
            </div>
          </div>

          <div className="preview-footer">
            <p className="footer-note">
              💡 <strong>Tip:</strong> Share this report with your team during bi-weekly review meetings to track progress and align on action items.
            </p>
            <button className="download-btn-large" onClick={handleDownloadReport}>
              <span className="btn-icon">📥</span>
              Generate & Download Report
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ReportGenerator;

